import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import moment from "moment";
import { PaginationLimit } from "../../../Config";
import { truncateText } from "../../../helpers";
import TenantMiniProfile from "../../LandlordPortal/Tenant/TenantMiniProfile";
import { FaCheck, FaTimes } from "react-icons/fa";

const Table = ({ apiData, isMovingOut }) => {
  const storageKey = `tenant_active_page`;
  const [data, setData] = useState([
    {
      name: "",
      property: "",
      moveInOutDate: "",
      compliance: "",
    },
  ]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  // Load saved page index
  const storageValue = sessionStorage.getItem(storageKey);
  const savedPage = storageValue !== null ? Number(storageValue) : 0;
  const [currentPage, setCurrentPage] = useState(savedPage);
  useEffect(() => {
    setCurrentPage(savedPage);
  }, [savedPage]);

  useEffect(() => {
    if (apiData) {
      const tempData = apiData.map((item) => ({
        name: getName(item),
        property: getPropertyName(item),
        moveInOutDate:
          isMovingOut === "M" ? (
            <MoveOutData>{item.moveOutDate}</MoveOutData>
          ) : (
            moveInOutDate(item)
          ),
        compliance: getCompliance(item),
      }));
      setData(tempData);
    }
  }, [apiData, openActionMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("#menuOptions") &&
        !event.target.closest(".action-btn")
      ) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---------- Name cell (TenantMiniProfile) ----------
  const getName = (item) => {
    return (
      <TenantMiniProfile
        tenantId={item.tenantId}
        name={item.tenantName}
        profilePicture={item.profilePicture}
        kycStatus={item.kycStatus}
        isPoliceVerified={item.isPoliceVerified}
        isRentAgreementSigned={item.isRentAgreementSigned}
        flatName={
          <div className="text-xs leading-5">
            <div>Room: {item?.roomNum}</div>
            {item?.flatFloorName && (
              <div>Floor: {item?.flatFloorName}</div>
            )}
            <div>Property: {item?.propName}</div>
          </div>
        }
      />
    );
  };

  // ---------- Property cell ----------
  const getPropertyName = (item) => {
    return (
      <div className="flex flex-col gap-0.5">
        <span
          className="text-gray-600 text-sm truncate"
          title={item.propName}
        >
          {truncateText(item.propName, 28)}
        </span>
        {item?.roomNum && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            Room {item.roomNum}
          </span>
        )}
      </div>
    );
  };

  // ---------- Move-In / Move-Out date cell ----------
  const moveInOutDate = (item) => {
    return (
      <div className="flex gap-3">
        <div className="flex flex-col text-sm text-gray-600">
          <span>
            {item.moveInDate
              ? moment(item.moveInDate).format("DD, MMM YYYY")
              : "-"}
          </span>
        </div>
      </div>
    );
  };

  // ---------- Compliance cell (PV + RA combined) ----------
  const getCompliance = (item) => {
    const pvOk = item?.isPoliceVerified === 1;
    const raOk = item?.isRentAgreementSigned === 1;

    const Badge = ({ ok, label }) => (
      <span
        className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border ${
          ok
            ? "bg-green-50 text-green-700 border-green-100"
            : "bg-gray-50 text-gray-500 border-gray-200"
        }`}>
        {ok ? <FaCheck size={10} /> : <FaTimes size={10} />}
        {label}
      </span>
    );

    return (
      <div className="flex items-center gap-1.5">
        <Badge ok={pvOk} label="PV" />
        <Badge ok={raOk} label="RA" />
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ---------- Columns ----------
  const columns = React.useMemo(() => {
    if (isMobileView) {
      return [
        {
          Header: "Name",
          accessor: "name",
        },
        {
          Header: "Property",
          accessor: "property",
        },
      ];
    }
    return [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Property",
        accessor: "property",
      },
      {
        Header: isMovingOut === "M" ? "Moving Out" : "Move-In",
        accessor: "moveInOutDate",
      },
      {
        Header: "Compliance",
        accessor: "compliance",
      },
    ];
  }, [isMobileView, isMovingOut]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    pageOptions,
    page,
    state: { pageIndex },
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
  } = useTable(
    {
      data,
      columns,
      initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
    },
    usePagination
  );

  useEffect(() => {
    sessionStorage.setItem(storageKey, pageIndex);
  }, [pageIndex, storageKey]);

  return (
    <>
      <CustomTable {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th {...column.getHeaderProps()}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted ? (
                      <img
                        src={Images.Arrow}
                        alt="down arrow"
                        className={`${
                          column.isSortedDesc ? "-rotate-90" : "rotate-90"
                        } w-1.5 inline-block ml-1.5`}
                      />
                    ) : (
                      ""
                    )}
                  </span>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody
          {...getTableBodyProps()}
          hasdata={(apiData?.length > 0).toString()}
        >
          {page.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <React.Fragment key={rowIndex}>
                <Tr
                  {...row.getRowProps({ style: { height: "80px" } })}
                  hasdata={(apiData?.length > 0).toString()}
                >
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <Td {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </Td>
                    );
                  })}
                </Tr>
                {selectedRow === rowIndex && window.innerWidth < 768 && (
                  <Tr>
                    <Td colSpan={columns.length}>
                      <ul className="flex flex-col gap-2">
                        <li>
                          <strong>Move-In</strong>{" "}
                          {data[rowIndex].moveInOutDate}
                        </li>
                      </ul>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </CustomTable>
      {apiData?.length > 0 && (
        <PaginationWrapper>
          <div className="px-2">
            Page{" "}
            <em>
              {pageIndex + 1} of {pageOptions.length}
            </em>
          </div>

          <div className="flex gap-1">
            <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
              <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
            </PrevBtn>
            <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
              <img
                src={Images.Arrow}
                alt="arrow"
                className="w-2 rotate-180 opacity-75"
              />
            </NextBtn>
          </div>
        </PaginationWrapper>
      )}
    </>
  );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) =>
  props.hasdata === "true" ? "border-b" : ""}`;
const Tr = tw.tr`
  border-b
  hover:bg-slate-50
  transition
`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const MoveOutData = tw.p`text-gray-600 text-sm bg-amber-100 px-3 py-1 rounded-md inline-flex`;

export default Table;